import { useSearch } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';

import { type ApiClientError } from '../api/client.js';
import {
  useChannels,
  useStores,
  useContacts,
  useOperators,
  useTicket,
  useTicketList,
  useAssignTicket,
  useUpdateTicket,
} from '../api/queries.js';
import { useAuthStore } from '../auth/auth-store.js';
import { IconButton } from '../components/ui/index.js';
import { ResizableSplit } from '../components/ui/split.js';
import { StoreTabs } from '../inbox/store-tabs.js';
import { ChatPanel } from '../inbox/chat-panel.js';
import { ContactPanel } from '../inbox/contact-panel.js';
import { TicketList } from '../inbox/ticket-list.js';

import '../inbox/inbox.css';

type FilterTab = 'mine' | 'all' | 'unassigned';
type SortKey = 'last_message_at' | 'created_at' | 'sla_deadline' | 'priority';
type SortDir = 'asc' | 'desc';
const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'new', label: 'Новый' },
  { value: 'open', label: 'В работе' },
  { value: 'pending', label: 'Ожидает' },
  { value: 'resolved', label: 'Решён' },
  { value: 'closed', label: 'Закрыт' },
];
const PRIORITY_OPTIONS: { value: string; label: string }[] = [
  { value: 'urgent', label: 'Срочно' },
  { value: 'high', label: 'Высокий' },
  { value: 'medium', label: 'Средний' },
  { value: 'low', label: 'Низкий' },
];
const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'last_message_at', label: 'Последнее сообщение' },
  { value: 'created_at', label: 'Дата создания' },
  { value: 'sla_deadline', label: 'Дедлайн SLA' },
  { value: 'priority', label: 'Приоритет' },
];

export function InboxPage(props: {
  /** Открыть сразу этот диалог (встроенный режим: переход из карточки заказа) */
  initialTicketId?: string | null;
  /** Открыть на вкладке магазина (встроенный режим со страницы магазина) */
  initialStoreId?: string | null;
} = {}): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const slug = user?.workspaceSlug;

  const [filter, setFilter] = useState<FilterTab>('all');
  // Вкладка магазина: '' — все, 'none' — общие каналы, иначе id магазина.
  const [store, setStore] = useState<string>(props.initialStoreId ?? '');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [priorityFilter, setPriorityFilter] = useState<string[]>([]);
  const [channelFilter, setChannelFilter] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<SortKey>('last_message_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const tbRef = useRef<HTMLDivElement>(null);
  // Меню рисуем как position:fixed от координат панели — иначе его обрезает
  // overflow:hidden у колонки списка (дропдаун был «невидим»).
  const menuStyle = (): React.CSSProperties => {
    const r = tbRef.current?.getBoundingClientRect();
    if (!r) return { display: 'none' };
    return {
      position: 'fixed',
      top: r.bottom + 4,
      right: Math.max(8, window.innerWidth - r.right),
      // ВАЖНО: класс .inbx-sidebar__menu задаёт left:100% и bottom:0 — без сброса
      // меню уезжало за правый край экрана. Перебиваем.
      left: 'auto',
      bottom: 'auto',
      width: 240,
      minWidth: 220,
      zIndex: 1000,
    };
  };
  const search = useSearch({ strict: false }) as { ticket?: string };
  const [activeTicketId, setActiveTicketId] = useState<string | null>(
    search.ticket ?? props.initialTicketId ?? null,
  );

  useEffect(() => {
    if (search.ticket) setActiveTicketId(search.ticket);
  }, [search.ticket]);

  const filterParams = useMemo<Record<string, string | string[]>>(() => {
    const p: Record<string, string | string[]> = { sort: `${sortBy}:${sortDir}` };
    if (store) p.store_id = store;
    if (filter === 'mine' && user?.id) p.assigned_to = user.id;
    if (filter === 'unassigned') p.assigned_to = 'unassigned';
    if (statusFilter.length) p.status = statusFilter;
    if (priorityFilter.length) p.priority = priorityFilter;
    if (channelFilter.length) p.channel_id = channelFilter;
    return p;
  }, [filter, user?.id, sortBy, sortDir, statusFilter, priorityFilter, channelFilter, store]);
  const activeFilterCount = statusFilter.length + priorityFilter.length + channelFilter.length;

  const ticketsQ = useTicketList(slug, filterParams);
  // Счётчики — в пределах выбранного магазина.
  const storeParam = store ? { store_id: store } : {};
  const myCount = useTicketList(slug, { assigned_to: user?.id ?? '', ...storeParam });
  const allCount = useTicketList(slug, storeParam);
  const unassignedCount = useTicketList(slug, { assigned_to: 'unassigned', ...storeParam });
  const channelsQ = useChannels(slug);
  const storesQ = useStores(slug);
  // В общем списке («Все магазины») у строки подписан её магазин; на вкладке
  // магазина подпись лишняя.
  const storeNames = useMemo(
    () => (store ? undefined : new Map((storesQ.data ?? []).map((s) => [s.id, s.name]))),
    [storesQ.data, store],
  );
  const operatorsQ = useOperators(slug);
  const contactsQ = useContacts(slug, '');
  const assignMut = useAssignTicket(slug);
  const updateMut = useUpdateTicket(slug);

  const channelsById = useMemo(
    () => new Map((channelsQ.data ?? []).map((c) => [c.id, c])),
    [channelsQ.data],
  );
  const operatorsById = useMemo(
    () => new Map((operatorsQ.data ?? []).map((o) => [o.id, o])),
    [operatorsQ.data],
  );
  const contactsById = useMemo(
    () => new Map((contactsQ.data ?? []).map((c) => [c.id, c])),
    [contactsQ.data],
  );

  const foundInList = useMemo(
    () => (ticketsQ.data ?? []).find((t) => t.id === activeTicketId) ?? null,
    [ticketsQ.data, activeTicketId],
  );
  // Deep-link (из истории контакта/поиска): тикет может быть вне текущего фильтра —
  // догружаем напрямую по id. Запрос включается только когда в списке его нет.
  const directTicketQ = useTicket(slug, activeTicketId && !foundInList ? activeTicketId : undefined);
  const activeTicket = foundInList ?? directTicketQ.data ?? null;

  // Состояния прямого запроса (для экрана «нет доступа» / «не найден»).
  const ticketErr = directTicketQ.error as ApiClientError | null;
  const ticketLoading = !!activeTicketId && !activeTicket && directTicketQ.isLoading;
  const ticketForbidden = !activeTicket && ticketErr?.status === 403;
  const ticketNotFound = !activeTicket && ticketErr?.status === 404;

  const activeContact = activeTicket ? contactsById.get(activeTicket.contactId) : undefined;
  const activeChannel = activeTicket ? channelsById.get(activeTicket.channelId) : undefined;
  const activeOperator = activeTicket?.assignedTo
    ? operatorsById.get(activeTicket.assignedTo)
    : undefined;

  return (
    <ResizableSplit id="inbox-cols" direction="row" sizes={[280, 0, 320]} mins={[220, 360, 240]}>
      <aside className="inbx-inbox__list" style={{ borderRight: '1px solid var(--inbx-border)' }}>
        <div className="inbx-tb" ref={tbRef}>
          <span className="inbx-tb__title">Входящие</span>
          <div className="inbx-tb__actions">
            <IconButton
              icon="ti-filter"
              tooltip={`Фильтры${activeFilterCount ? ` (${activeFilterCount})` : ''}`}
              onClick={() => {
                setFilterOpen((v) => !v);
                setSortOpen(false);
              }}
            />
            <IconButton
              icon={sortDir === 'asc' ? 'ti-sort-ascending' : 'ti-sort-descending'}
              tooltip="Сортировка"
              onClick={() => {
                setSortOpen((v) => !v);
                setFilterOpen(false);
              }}
            />
          </div>
          {filterOpen && (
            <div
              className="inbx-sidebar__menu"
              style={menuStyle()}
              onMouseLeave={() => setFilterOpen(false)}
            >
              <div className="inbx-sidebar__menu-header">Статус</div>
              {STATUS_OPTIONS.map((s) => {
                const on = statusFilter.includes(s.value);
                return (
                  <button
                    key={s.value}
                    type="button"
                    className="inbx-sidebar__menu-item"
                    onClick={() =>
                      setStatusFilter((cur) =>
                        on ? cur.filter((x) => x !== s.value) : [...cur, s.value],
                      )
                    }
                  >
                    <i className={on ? 'ti-checkbox' : 'ti-square'} /> {s.label}
                  </button>
                );
              })}
              <div className="inbx-sidebar__menu-divider" />
              <div className="inbx-sidebar__menu-header">Приоритет</div>
              {PRIORITY_OPTIONS.map((p) => {
                const on = priorityFilter.includes(p.value);
                return (
                  <button
                    key={p.value}
                    type="button"
                    className="inbx-sidebar__menu-item"
                    onClick={() =>
                      setPriorityFilter((cur) =>
                        on ? cur.filter((x) => x !== p.value) : [...cur, p.value],
                      )
                    }
                  >
                    <i className={on ? 'ti-checkbox' : 'ti-square'} /> {p.label}
                  </button>
                );
              })}
              {(channelsQ.data ?? []).length > 0 && (
                <>
                  <div className="inbx-sidebar__menu-divider" />
                  <div className="inbx-sidebar__menu-header">Источник</div>
                  {(channelsQ.data ?? []).map((c) => {
                    const on = channelFilter.includes(c.id);
                    return (
                      <button
                        key={c.id}
                        type="button"
                        className="inbx-sidebar__menu-item"
                        onClick={() =>
                          setChannelFilter((cur) =>
                            on ? cur.filter((x) => x !== c.id) : [...cur, c.id],
                          )
                        }
                      >
                        <i className={on ? 'ti-checkbox' : 'ti-square'} /> {c.name}
                      </button>
                    );
                  })}
                </>
              )}
              {activeFilterCount > 0 && (
                <>
                  <div className="inbx-sidebar__menu-divider" />
                  <button
                    type="button"
                    className="inbx-sidebar__menu-item"
                    onClick={() => {
                      setStatusFilter([]);
                      setPriorityFilter([]);
                      setChannelFilter([]);
                    }}
                  >
                    <i className="ti-x" /> Сбросить
                  </button>
                </>
              )}
            </div>
          )}
          {sortOpen && (
            <div
              className="inbx-sidebar__menu"
              style={menuStyle()}
              onMouseLeave={() => setSortOpen(false)}
            >
              <div className="inbx-sidebar__menu-header">Сортировать по</div>
              {SORT_OPTIONS.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  className="inbx-sidebar__menu-item"
                  onClick={() => {
                    setSortBy(s.value);
                    setSortOpen(false);
                  }}
                >
                  <i className={sortBy === s.value ? 'ti-circle-dot' : 'ti-circle'} /> {s.label}
                </button>
              ))}
              <div className="inbx-sidebar__menu-divider" />
              <button
                type="button"
                className="inbx-sidebar__menu-item"
                onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
              >
                <i className={sortDir === 'asc' ? 'ti-sort-ascending' : 'ti-sort-descending'} />
                {sortDir === 'asc' ? 'По возрастанию' : 'По убыванию'}
              </button>
            </div>
          )}
        </div>
        <StoreTabs workspaceSlug={slug} value={store} onChange={setStore} />
        <div className="inbx-filter-tabs">
          <button
            type="button"
            className={`inbx-filter-tabs__btn ${filter === 'mine' ? 'is-active' : ''}`}
            onClick={() => setFilter('mine')}
          >
            Мои <span className="inbx-filter-tabs__count">({myCount.data?.length ?? 0})</span>
          </button>
          <button
            type="button"
            className={`inbx-filter-tabs__btn ${filter === 'all' ? 'is-active' : ''}`}
            onClick={() => setFilter('all')}
          >
            Все <span className="inbx-filter-tabs__count">({allCount.data?.length ?? 0})</span>
          </button>
          <button
            type="button"
            className={`inbx-filter-tabs__btn ${filter === 'unassigned' ? 'is-active' : ''}`}
            onClick={() => setFilter('unassigned')}
          >
            Без оператора{' '}
            <span className="inbx-filter-tabs__count">({unassignedCount.data?.length ?? 0})</span>
          </button>
        </div>
        <TicketList
          tickets={ticketsQ.data}
          loading={ticketsQ.isLoading}
          activeId={activeTicketId}
          onSelect={setActiveTicketId}
          contactsById={contactsById}
          channelsById={channelsById}
          storeNames={storeNames}
          currentUserId={user?.id}
          onAssignSelf={(id) => {
            if (user?.id) assignMut.mutate({ id, userId: user.id });
          }}
          onClose={(id) => {
            if (confirm('Закрыть тикет?')) updateMut.mutate({ id, patch: { status: 'closed' } });
          }}
        />
      </aside>

      <section className="inbx-inbox__chat">
        {activeTicket ? (
          <ChatPanel
            ticket={activeTicket}
            contact={activeContact}
            channel={activeChannel}
            assignedOperator={activeOperator}
            operators={operatorsQ.data ?? []}
            workspaceSlug={slug}
            currentUserId={user?.id}
          />
        ) : ticketLoading ? (
          <div className="inbx-empty" style={{ flex: 1 }}>
            <i className="ti-loader-2 inbx-empty__icon" />
            <div className="inbx-empty__title">Загрузка тикета…</div>
          </div>
        ) : ticketForbidden ? (
          <div className="inbx-empty" style={{ flex: 1 }}>
            <i className="ti-lock inbx-empty__icon" />
            <div className="inbx-empty__title">У вас нет доступа</div>
            <div className="inbx-empty__desc">
              У вашей учётной записи нет прав на просмотр этого тикета
            </div>
          </div>
        ) : ticketNotFound ? (
          <div className="inbx-empty" style={{ flex: 1 }}>
            <i className="ti-search-off inbx-empty__icon" />
            <div className="inbx-empty__title">Тикет не найден</div>
            <div className="inbx-empty__desc">Возможно, он был удалён или объединён</div>
          </div>
        ) : (
          <div className="inbx-empty" style={{ flex: 1 }}>
            <i className="ti-message-circle inbx-empty__icon" />
            <div className="inbx-empty__title">Выберите тикет слева</div>
            <div className="inbx-empty__desc">Чтобы начать переписку с клиентом</div>
          </div>
        )}
      </section>

      <aside className="inbx-inbox__contact" style={{ borderLeft: '1px solid var(--inbx-border)' }}>
        {activeTicket ? (
          <ContactPanel
            workspaceSlug={slug}
            ticket={activeTicket}
            contactId={activeTicket.contactId}
            channel={activeChannel}
            assignedOperator={activeOperator}
          />
        ) : (
          <div className="inbx-empty">
            <i className="ti-user inbx-empty__icon" />
            <div className="inbx-empty__desc">Карточка контакта</div>
          </div>
        )}
      </aside>
    </ResizableSplit>
  );
}
